import { getInitialState, getTransitionActions, getTransitionCondition, getTransitionTarget, isEventlessTransition, isServiceNode, makeArray } from '../util';
const ACTIVE_CLASS = 'fsmActive';
function sanitizeId(name) {
  const replaced = name.replace(/[^a-zA-Z0-9_]/g, '_') || 'state';
  return /^[0-9]/.test(replaced) ? `s_${replaced}` : replaced;
}
function buildIdMap(names) {
  const map = {};
  const used = new Set();
  names.forEach(name => {
    const base = sanitizeId(name);
    let candidate = base;
    let suffix = 1;
    while (used.has(candidate)) {
      candidate = `${base}_${suffix}`;
      suffix += 1;
    }
    used.add(candidate);
    map[name] = candidate;
  });
  return map;
}
function buildLabel(event, transition) {
  const cond = getTransitionCondition(transition);
  const actions = getTransitionActions(transition);
  const head = [event, cond ? `[${cond}]` : ''].filter(Boolean).join(' ');
  if (actions.length === 0) {
    return head;
  }
  const tail = `/ ${actions.join(', ')}`;
  return head ? `${head} ${tail}` : tail;
}
function transitionLine(fromId, event, transition, resolveId) {
  const target = getTransitionTarget(transition);
  const toId = target ? resolveId(target) : fromId;
  const label = buildLabel(event, transition);
  return label ? `  ${fromId} --> ${toId} : ${label}` : `  ${fromId} --> ${toId}`;
}

/**
 * Walks a MachineDefinition and produces Mermaid `stateDiagram-v2` source so a
 * machine can be rendered as a state chart. Pure and dependency-free.
 */
export function transitionsToMermaid(definition, options = {}) {
  const {
    activeState
  } = options;
  const stateNames = Object.keys(definition.states);
  const idMap = buildIdMap(stateNames);
  const resolveId = name => idMap[name] || sanitizeId(name);
  const lines = ['stateDiagram-v2'];
  stateNames.forEach(name => {
    lines.push(`  state "${name}" as ${resolveId(name)}`);
  });
  const initial = getInitialState(definition);
  if (initial) {
    lines.push(`  [*] --> ${resolveId(initial)}`);
  }
  stateNames.forEach(name => {
    const node = definition.states[name];
    const fromId = resolveId(name);
    const serviceNode = isServiceNode(node);
    const noteParts = [];
    if (serviceNode) {
      const {
        invoke
      } = node;
      if (typeof invoke.src === 'string') {
        noteParts.push(`invokes ${invoke.src}`);
      }
    }
    if (node.tags && node.tags.length > 0) {
      noteParts.push(`tags: ${node.tags.join(', ')}`);
    }
    if (noteParts.length > 0) {
      lines.push(`  note right of ${fromId} : ${noteParts.join('; ')}`);
    }
    if (serviceNode) {
      const {
        invoke
      } = node;
      if (invoke.onDone) {
        makeArray(invoke.onDone).forEach(transition => {
          lines.push(transitionLine(fromId, 'onDone', transition, resolveId));
        });
      }
      if (invoke.onError) {
        makeArray(invoke.onError).forEach(transition => {
          lines.push(transitionLine(fromId, 'onError', transition, resolveId));
        });
      }
    }
    if (isEventlessTransition(node)) {
      const {
        always
      } = node;
      makeArray(always).forEach(transition => {
        lines.push(transitionLine(fromId, 'always', transition, resolveId));
      });
    }
    const {
      on
    } = node;
    if (on) {
      Object.keys(on).forEach(event => {
        const transition = on[event];
        if (!transition) {
          return;
        }
        makeArray(transition).forEach(singleTransition => {
          lines.push(transitionLine(fromId, event, singleTransition, resolveId));
        });
      });
    }
  });
  const topLevelOn = definition.on;
  if (topLevelOn) {
    Object.keys(topLevelOn).forEach(event => {
      const transition = topLevelOn[event];
      if (!transition) {
        return;
      }
      const transitions = makeArray(transition);
      stateNames.forEach(name => {
        var _on;
        const node = definition.states[name];
        const handledLocally = Boolean((_on = node.on) === null || _on === void 0 ? void 0 : _on[event]);
        if (handledLocally) {
          return;
        }
        transitions.forEach(singleTransition => {
          lines.push(transitionLine(resolveId(name), event, singleTransition, resolveId));
        });
      });
    });
  }
  if (activeState && idMap[activeState]) {
    lines.push(`  classDef ${ACTIVE_CLASS} fill:#c8e6f9,stroke:#0096dd,stroke-width:3px,color:#00356b`);
    lines.push(`  class ${resolveId(activeState)} ${ACTIVE_CLASS}`);
  }
  return lines.join('\n');
}